/**
 * 桥接工具函数 - 使用 viem 直接与合约交互
 * 替代 Optimism SDK 的 CrossChainMessenger
 */

import { encodeFunctionData, parseEther, parseUnits } from 'viem';
import { getWithdrawals } from 'viem/op-stack';
import { createL1PublicClient, createL2PublicClient, createL1WalletClient, createL2WalletClient, createEthereumTransport } from './viemClient';

// 合约 ABI
const L1_STANDARD_BRIDGE_ABI = [
    {
        inputs: [
            { name: '_l1Token', type: 'address' },
            { name: '_l2Token', type: 'address' },
            { name: '_amount', type: 'uint256' },
            { name: '_minGasLimit', type: 'uint32' },
            { name: '_extraData', type: 'bytes' },
        ],
        name: 'depositERC20',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            { name: '_l1Token', type: 'address' },
            { name: '_l2Token', type: 'address' },
            { name: '_amount', type: 'uint256' },
            { name: '_minGasLimit', type: 'uint32' },
            { name: '_extraData', type: 'bytes' },
        ],
        name: 'depositERC20To',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            { name: '_minGasLimit', type: 'uint32' },
            { name: '_extraData', type: 'bytes' },
        ],
        name: 'depositETH',
        outputs: [],
        stateMutability: 'payable',
        type: 'function',
    },
    {
        inputs: [
            { name: '_to', type: 'address' },
            { name: '_minGasLimit', type: 'uint32' },
            { name: '_extraData', type: 'bytes' },
        ],
        name: 'depositETHTo',
        outputs: [],
        stateMutability: 'payable',
        type: 'function',
    },
];

const L2_STANDARD_BRIDGE_ABI = [
    {
        inputs: [
            { name: '_l2Token', type: 'address' },
            { name: '_amount', type: 'uint256' },
            { name: '_minGasLimit', type: 'uint32' },
            { name: '_extraData', type: 'bytes' },
        ],
        name: 'withdraw',
        outputs: [],
        stateMutability: 'payable',
        type: 'function',
    },
    {
        inputs: [
            { name: '_l2Token', type: 'address' },
            { name: '_to', type: 'address' },
            { name: '_amount', type: 'uint256' },
            { name: '_minGasLimit', type: 'uint32' },
            { name: '_extraData', type: 'bytes' },
        ],
        name: 'withdrawTo',
        outputs: [],
        stateMutability: 'payable',
        type: 'function',
    },
];

const LEGACY_ERC20_ETH = '0xDeadDeAddeAddEAddeadDEaDDEAdDeaDDeAD0000';

const ERC20_ABI = [
    {
        inputs: [
            { name: 'spender', type: 'address' },
            { name: 'amount', type: 'uint256' },
        ],
        name: 'approve',
        outputs: [{ name: '', type: 'bool' }],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            { name: 'owner', type: 'address' },
            { name: 'spender', type: 'address' },
        ],
        name: 'allowance',
        outputs: [{ name: '', type: 'uint256' }],
        stateMutability: 'view',
        type: 'function',
    },
];

/**
 * 存款 ETH 到 L2
 * @param {string} amount - ETH 数量（字符串格式，如 "1.0"）
 * @param {string} accountAddress - 账户地址
 * @returns {Promise<`0x${string}`>} 交易哈希
 */
export const depositETH = async (amount, accountAddress) => {
    const l1BridgeAddress = process.env.REACT_APP_L1_STANDARD_BRIDGE_PROXY;
    if (!l1BridgeAddress) {
        throw new Error('REACT_APP_L1_STANDARD_BRIDGE_PROXY 未配置');
    }

    const transport = createEthereumTransport();
    const walletClient = createL1WalletClient(transport);

    const value = parseEther(amount);
    const minGasLimit = 200000; // 默认最小 gas limit

    const hash = await walletClient.writeContract({
        account: accountAddress,
        address: l1BridgeAddress,
        abi: L1_STANDARD_BRIDGE_ABI,
        functionName: 'depositETH',
        args: [minGasLimit, '0x'],
        value,
    });

    return hash;
};

/**
 * 存款 ERC20 代币到 L2
 * @param {string} l1Token - L1 代币地址
 * @param {string} l2Token - L2 代币地址
 * @param {string} amount - 代币数量（字符串格式）
 * @param {number} decimals - 代币精度（默认 18）
 * @param {string} accountAddress - 账户地址
 * @returns {Promise<`0x${string}`>} 交易哈希
 */
export const depositERC20 = async (l1Token, l2Token, amount, decimals = 18, accountAddress) => {
    const l1BridgeAddress = process.env.REACT_APP_L1_STANDARD_BRIDGE_PROXY;
    if (!l1BridgeAddress) {
        throw new Error('REACT_APP_L1_STANDARD_BRIDGE_PROXY 未配置');
    }

    const transport = createEthereumTransport();
    const walletClient = createL1WalletClient(transport);
    const publicClient = createL1PublicClient();

    // 1. 检查并批准代币
    const parsedAmount = parseUnits(amount, decimals);
    const allowance = await publicClient.readContract({
        address: l1Token,
        abi: ERC20_ABI,
        functionName: 'allowance',
        args: [accountAddress, l1BridgeAddress],
    });

    if (allowance < parsedAmount) {
        // 需要批准
        const approveHash = await walletClient.writeContract({
            account: accountAddress,
            address: l1Token,
            abi: ERC20_ABI,
            functionName: 'approve',
            args: [l1BridgeAddress, parsedAmount],
        });

        // 等待批准交易确认
        await publicClient.waitForTransactionReceipt({ hash: approveHash });
    }

    // 2. 存款代币
    const minGasLimit = 200000;
    const hash = await walletClient.writeContract({
        account: accountAddress,
        address: l1BridgeAddress,
        abi: L1_STANDARD_BRIDGE_ABI,
        functionName: 'depositERC20',
        args: [l1Token, l2Token, parsedAmount, minGasLimit, '0x'],
    });

    return hash;
};

/**
 * 从 L2 提取 ETH
 * @param {string} amount - ETH 数量（字符串格式）
 * @param {string} accountAddress - 账户地址
 * @returns {Promise<`0x${string}`>} 交易哈希
 */
export const withdrawETH = async (amount, accountAddress) => {
    const l2BridgeAddress = process.env.REACT_APP_L2_BRIDGE || '0x4200000000000000000000000000000000000010';
    
    const transport = createEthereumTransport();
    const walletClient = createL2WalletClient(transport);

    const value = parseEther(amount);
    const minGasLimit = 200000;

    const args = [LEGACY_ERC20_ETH, value, minGasLimit, '0x'];
    const data = encodeFunctionData({
        abi: L2_STANDARD_BRIDGE_ABI,
        functionName: 'withdraw',
        args
    });

    console.log('[bridge.withdrawETH] writeContract request', {
        bridge: l2BridgeAddress,
        account: accountAddress,
        amount,
        value: value.toString(),
        minGasLimit,
        args,
        data
    });

    const hash = await walletClient.writeContract({
        account: accountAddress,
        address: l2BridgeAddress,
        abi: L2_STANDARD_BRIDGE_ABI,
        functionName: 'withdraw',
        args,
        value,
    });

    return hash;
};

/**
 * 从 L2 提取 ERC20 代币
 * @param {string} l1Token - L1 代币地址
 * @param {string} l2Token - L2 代币地址
 * @param {string} amount - 代币数量（字符串格式）
 * @param {number} decimals - 代币精度（默认 18）
 * @param {string} accountAddress - 账户地址
 * @returns {Promise<`0x${string}`>} 交易哈希
 */
export const withdrawERC20 = async (l1Token, l2Token, amount, decimals = 18, accountAddress) => {
    const l2BridgeAddress = process.env.REACT_APP_L2_BRIDGE || '0x4200000000000000000000000000000000000010';
    
    const transport = createEthereumTransport();
    const walletClient = createL2WalletClient(transport);

    const parsedAmount = parseUnits(amount, decimals);
    const minGasLimit = 200000;

    const args = [l2Token, parsedAmount, minGasLimit, '0x'];
    const data = encodeFunctionData({
        abi: L2_STANDARD_BRIDGE_ABI,
        functionName: 'withdraw',
        args
    });

    console.log('[bridge.withdrawERC20] writeContract request', {
        bridge: l2BridgeAddress,
        account: accountAddress,
        l1Token,
        l2Token,
        amount,
        parsedAmount: parsedAmount.toString(),
        minGasLimit,
        args,
        data
    });

    const hash = await walletClient.writeContract({
        account: accountAddress,
        address: l2BridgeAddress,
        abi: L2_STANDARD_BRIDGE_ABI,
        functionName: 'withdraw',
        args,
    });

    return hash;
};

// 事件 ABI
const DEPOSIT_INITIATED_ABI = {
    inputs: [
        { indexed: true, name: 'l1Token', type: 'address' },
        { indexed: true, name: 'l2Token', type: 'address' },
        { indexed: true, name: 'from', type: 'address' },
        { indexed: false, name: 'to', type: 'address' },
        { indexed: false, name: 'amount', type: 'uint256' },
        { indexed: false, name: 'extraData', type: 'bytes' },
    ],
    name: 'DepositInitiated',
    type: 'event',
};

const WITHDRAWAL_INITIATED_ABI = {
    inputs: [
        { indexed: true, name: 'l1Token', type: 'address' },
        { indexed: true, name: 'l2Token', type: 'address' },
        { indexed: true, name: 'from', type: 'address' },
        { indexed: false, name: 'to', type: 'address' },
        { indexed: false, name: 'amount', type: 'uint256' },
        { indexed: false, name: 'extraData', type: 'bytes' },
    ],
    name: 'WithdrawalInitiated',
    type: 'event',
};

/**
 * 查询存款交易
 * @param {string} address - 用户地址
 * @returns {Promise<Array>} 存款交易列表
 */
export const getDepositsByAddress = async (address) => {
    const l1BridgeAddress = process.env.REACT_APP_L1_STANDARD_BRIDGE_PROXY;
    if (!l1BridgeAddress) {
        throw new Error('REACT_APP_L1_STANDARD_BRIDGE_PROXY 未配置');
    }

    const publicClient = createL1PublicClient();

    // 查询 DepositInitiated 事件
    const logs = await publicClient.getLogs({
        address: l1BridgeAddress,
        event: DEPOSIT_INITIATED_ABI,
        args: {
            from: address,
        },
        fromBlock: 'earliest',
        toBlock: 'latest',
    });

    // 转换日志为交易数据格式
    return logs.map((log) => ({
        transactionHash: log.transactionHash,
        blockNumber: Number(log.blockNumber),
        amount: log.args.amount,
        l1Token: log.args.l1Token,
        l2Token: log.args.l2Token,
        from: log.args.from,
        to: log.args.to,
    }));
};

/**
 * 查询提款交易
 * @param {string} address - 用户地址
 * @returns {Promise<Array>} 提款交易列表
 */
export const getWithdrawalsByAddress = async (address) => {
    const l2BridgeAddress = process.env.REACT_APP_L2_BRIDGE || '0x4200000000000000000000000000000000000010';
    
    const publicClient = createL2PublicClient();

    // 查询 WithdrawalInitiated 事件
    const logs = await publicClient.getLogs({
        address: l2BridgeAddress,
        event: WITHDRAWAL_INITIATED_ABI,
        args: {
            from: address,
        },
        fromBlock: 'earliest',
        toBlock: 'latest',
    });

    // 转换日志为交易数据格式
    return logs.map((log) => ({
        transactionHash: log.transactionHash,
        blockNumber: Number(log.blockNumber),
        amount: log.args.amount,
        l1Token: log.args.l1Token,
        l2Token: log.args.l2Token,
        from: log.args.from,
        to: log.args.to,
    }));
};

